import { useState } from 'react';
import { LuX } from 'react-icons/lu';
import { AlertDialog, AlertDialogContent } from '@/components/ui/alert-dialog';
import { Button } from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import { Tag, type TagColor } from '@/components/Tag';
import useCompanyTagsList from '../hooks/useCompanyTagsList';
import useBulkRemoveCompanyTags from '../api/useBulkRemoveCompanyTags';
import { showSuccessToast, showErrorToast } from '../../job_change_processes/lib/toast';
import { MdSettings } from 'react-icons/md';

type Props = {
  ids: number[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

export default function CompanyBulkRemoveTagModal({ ids, open, onOpenChange, onSuccess }: Props) {
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const { data } = useCompanyTagsList();
  const { mutateAsync: bulkRemoveTags, isPending } = useBulkRemoveCompanyTags();

  const handleTagToggle = (tagId: number, checked: boolean) => {
    setSelectedTagIds((prev) =>
      checked ? (prev.includes(tagId) ? prev : [...prev, tagId]) : prev.filter((id) => id !== tagId)
    );
  };

  const handleDeselectAll = () => {
    setSelectedTagIds([]);
  };

  const handleSubmit = async () => {
    if (selectedTagIds.length === 0) return;
    try {
      await bulkRemoveTags({ ids, custom_tag_ids: selectedTagIds });
      showSuccessToast('タグを解除しました');
      setSelectedTagIds([]);
      onSuccess();
    } catch {
      showErrorToast('タグの解除に失敗しました');
    }
  };

  const handleClose = () => {
    setSelectedTagIds([]);
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="w-[860px]! max-w-[860px]!">
        {/* ヘッダー */}
        <div className="flex items-center justify-between border-b border-solid border-border px-6 py-4">
          <p className="text-heading-xs font-bold">解除するタグを選択</p>
          <button
            type="button"
            className={`flex cursor-pointer items-center gap-1 border-none bg-transparent px-3 py-2.5 text-sm font-bold text-primary ${
              selectedTagIds.length === 0 ? 'opacity-50' : ''
            }`}
            onClick={handleDeselectAll}
            disabled={selectedTagIds.length === 0}
          >
            <LuX className="size-5" />
            全ての選択を解除
          </button>
        </div>

        {/* タグ選択エリア */}
        <div className="px-6 py-4">
          {data?.tags && (
            <div className="flex flex-wrap gap-x-8 gap-y-4">
              {data.tags.map((tag) => (
                <div key={tag.id} className="w-[100px]">
                  <Checkbox
                    value={tag.id}
                    checked={selectedTagIds.includes(tag.id)}
                    onChange={(checked) => handleTagToggle(tag.id, checked)}
                  >
                    <Tag color={tag.colorHex as TagColor} size="md">
                      {tag.name}
                    </Tag>
                  </Checkbox>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* フッター */}
        <div className="flex items-center justify-between border-t border-solid border-border px-6 py-4">
          <a href="/agent/company_tags" target="_blank">
            <Button color="tertiary" size="sm" className="w-[96px] justify-start">
              <MdSettings size={16} />
              <span>タグを管理</span>
            </Button>
          </a>
          <div className="flex items-center gap-4">
            <Button type="button" color="secondary" size="md" onClick={handleClose}>
              キャンセル
            </Button>
            <Button
              type="button"
              color="danger"
              size="md"
              onClick={handleSubmit}
              disabled={selectedTagIds.length === 0 || isPending}
              className={selectedTagIds.length === 0 || isPending ? 'opacity-50' : ''}
            >
              解除する
            </Button>
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
